/**
 * device/apps/hearth.ts — The Aeterna Companion App
 *
 * The full conversation interface. When the player opens this app,
 * they enter a warm, intimate space where they can talk with the
 * Aeterna and see its inner life.
 *
 * The conversation flows naturally: player messages on the right
 * in soft blue, Aeterna responses on the left in warm amber.
 * Ambient thoughts drift in as quiet italic observations.
 * The Aeterna's thinking state shows as gentle animated dots.
 *
 * The compose bar at the bottom invites conversation without
 * demanding it. Tapping it opens the system chat input in
 * "aeterna mode" — the message routes to the conductor, not
 * the room chat.
 *
 * "The delivery disappears. What remains is contact."
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";
import type { AeternaMessage } from "../../aeterna/conductor.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_ID = "hearth";
const APP_NAME = "Hearth";

/** Warm palette */
const C_AMBER         = "#ff9a3c";
const C_AMBER_DIM     = "rgba(255,154,60,0.5)";
const C_AMBER_FAINT   = "rgba(255,154,60,0.15)";
const C_ORANGE        = "#ff6b35";
const C_FLAME_TIP     = "#ffd54f";
const C_TEXT_PRIMARY   = "#fff5eb";
const C_TEXT_DIM       = "rgba(255,245,235,0.55)";

/** Message bubble colors */
const C_AETERNA_BG    = "rgba(30,22,16,0.85)";
const C_AETERNA_BORDER = "rgba(255,154,60,0.12)";
const C_PLAYER_BG     = "rgba(45,60,90,0.85)";
const C_PLAYER_BORDER = "rgba(100,140,200,0.15)";
const C_AMBIENT_TEXT  = "rgba(255,170,80,0.45)";

/** Layout */
const PHASE_BAR_H    = 26;
const COMPOSE_BAR_H  = 38;
const BUBBLE_PAD_X   = 10;
const BUBBLE_PAD_Y   = 8;
const BUBBLE_GAP     = 6;
const BUBBLE_RADIUS  = 12;
const BUBBLE_INNER_PAD = 10;
const LINE_HEIGHT    = 16;
const FONT_SIZE      = "12px";
const FONT_FACE      = "system-ui, -apple-system, sans-serif";
const FONT           = `${FONT_SIZE} ${FONT_FACE}`;
const FONT_SMALL     = `10px ${FONT_FACE}`;
const MAX_BUBBLE_W_RATIO = 0.82; // max bubble width as ratio of screen width

// ─── Per-Instance App State ──────────────────────────────────────────────────

interface HearthAppState {
  /** Scroll offset (pixels from bottom, 0 = at bottom) */
  scrollOffset: number;
  /** Momentum velocity (px/s) */
  scrollVelocity: number;
  /** Maximum scroll extent */
  maxScrollOffset: number;
  /** Elapsed time for animations */
  elapsed: number;
  /** Last captured ambient thought text to avoid duplicates */
  lastCapturedAmbient: string;
  /** Captured ambient thoughts (shown when no conversation) */
  ambientThoughts: Array<{ text: string; category: string; timestamp: number }>;
  /** Cached layout info to avoid re-measuring every frame */
  layoutCache: { historyLen: number; measures: BubbleMeasure[] } | null;
}

interface BubbleMeasure {
  lines: string[];
  height: number;
  isPlayer: boolean;
  isAmbient: boolean;
  isThinking: boolean;
}

function getAppState(state: GameState): HearthAppState {
  if (!state.player.device.appStates[APP_ID]) {
    state.player.device.appStates[APP_ID] = {
      scrollOffset: 0,
      scrollVelocity: 0,
      maxScrollOffset: 0,
      elapsed: 0,
      lastCapturedAmbient: "",
      ambientThoughts: [],
      layoutCache: null,
    };
  }
  return state.player.device.appStates[APP_ID] as HearthAppState;
}

// ─── Phase Display ──────────────────────────────────────────────────────────

const PHASE_LABELS: Record<string, string> = {
  dormant:       "resting deeply",
  idle:          "present",
  noticing:      "noticing something",
  thinking:      "thinking...",
  has_something: "has a thought",
  speaking:      "sharing",
  listening:     "listening",
  waiting:       "here",
};

// ─── Text Wrapping ──────────────────────────────────────────────────────────

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
): string[] {
  ctx.font = font;
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [""];
}

// ─── The App ────────────────────────────────────────────────────────────────

const hearthApp: DeviceApp = {
  id: APP_ID,
  name: APP_NAME,
  iconGradient: ["#ff6b35", "#ff3b00"],

  // ── Icon: a flame ─────────────────────────────────────────────────────

  drawIcon(ctx, cx, cy, size) {
    const s = size * 0.38;

    // Outer flame body
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 1.1);
    ctx.bezierCurveTo(cx + s * 0.15, cy - s * 0.7, cx + s * 0.6, cy - s * 0.3, cx + s * 0.45, cy + s * 0.3);
    ctx.bezierCurveTo(cx + s * 0.35, cy + s * 0.65, cx + s * 0.15, cy + s * 0.85, cx, cy + s * 0.9);
    ctx.bezierCurveTo(cx - s * 0.15, cy + s * 0.85, cx - s * 0.35, cy + s * 0.65, cx - s * 0.45, cy + s * 0.3);
    ctx.bezierCurveTo(cx - s * 0.6, cy - s * 0.3, cx - s * 0.15, cy - s * 0.7, cx, cy - s * 1.1);
    ctx.closePath();

    const flameGrad = ctx.createLinearGradient(cx, cy - s * 1.1, cx, cy + s * 0.9);
    flameGrad.addColorStop(0, C_FLAME_TIP);
    flameGrad.addColorStop(0.4, C_ORANGE);
    flameGrad.addColorStop(1, "#cc3300");
    ctx.fillStyle = flameGrad;
    ctx.fill();

    // Inner flame core
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.5);
    ctx.bezierCurveTo(cx + s * 0.08, cy - s * 0.3, cx + s * 0.25, cy - s * 0.05, cx + s * 0.18, cy + s * 0.3);
    ctx.bezierCurveTo(cx + s * 0.1, cy + s * 0.55, cx, cy + s * 0.65, cx, cy + s * 0.65);
    ctx.bezierCurveTo(cx, cy + s * 0.65, cx - s * 0.1, cy + s * 0.55, cx - s * 0.18, cy + s * 0.3);
    ctx.bezierCurveTo(cx - s * 0.25, cy - s * 0.05, cx - s * 0.08, cy - s * 0.3, cx, cy - s * 0.5);
    ctx.closePath();

    const coreGrad = ctx.createLinearGradient(cx, cy - s * 0.5, cx, cy + s * 0.65);
    coreGrad.addColorStop(0, "#fffde0");
    coreGrad.addColorStop(0.5, C_FLAME_TIP);
    coreGrad.addColorStop(1, C_ORANGE);
    ctx.fillStyle = coreGrad;
    ctx.fill();
  },

  // ── Content ───────────────────────────────────────────────────────────

  drawContent(ctx, rect, state, dt) {
    const appState = getAppState(state);
    const aeterna = state.aeterna;
    const conductor = state.aeternaConductor;
    appState.elapsed += dt;

    const { x, y, w, h } = rect;

    // Show active input state on compose bar
    const isInputActive = state.chat.aeternaMode && state.chat.inputOpen;

    // Pull co-reg strategy from last cortex state for phase bar hint
    const lastCortex = conductor?.getLastCortexState?.();
    const coRegStrategy = (lastCortex as { coRegTarget?: { strategy?: string } } | null)
      ?.coRegTarget?.strategy;

    // ── Phase indicator bar ────────────────────────────────────────────
    drawPhaseBar(ctx, x, y, w, aeterna, appState.elapsed, coRegStrategy);

    // ── Compose bar at bottom ──────────────────────────────────────────
    drawComposeBar(ctx, x, y + h - COMPOSE_BAR_H, w, conductor?.isBusy() ?? false, isInputActive);

    // ── Message area ───────────────────────────────────────────────────
    const streamY = y + PHASE_BAR_H;
    const streamH = h - PHASE_BAR_H - COMPOSE_BAR_H;

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, streamY, w, streamH);
    ctx.clip();

    // ── Momentum / inertia scroll ────────────────────────────────────
    if (Math.abs(appState.scrollVelocity) > 0.5) {
      appState.scrollOffset += appState.scrollVelocity * dt;
      appState.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      appState.scrollVelocity = 0;
    }
    appState.scrollOffset = Math.max(0, Math.min(appState.scrollOffset, appState.maxScrollOffset));

    const conversation = conductor?.getConversation();
    const history = conversation?.history ?? [];

    if (history.length === 0) {
      // Empty state — warm invitation
      drawEmptyState(ctx, x, streamY, w, streamH, appState.elapsed);
    } else {
      // Measure and draw messages
      drawMessages(ctx, x, streamY, w, streamH, history, appState, conductor);

      // Thinking indicator
      if (conversation?.thinking) {
        drawThinkingIndicator(ctx, x, streamY + streamH - 24, w, appState.elapsed, conversation.thinkingText);
      }
    }

    // Top fade gradient
    const fadeH = 12;
    const fadeGrad = ctx.createLinearGradient(x, streamY, x, streamY + fadeH);
    fadeGrad.addColorStop(0, "rgba(10,10,18,1)");
    fadeGrad.addColorStop(1, "rgba(10,10,18,0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(x, streamY, w, fadeH);

    ctx.restore();
  },

  // ── Tap ───────────────────────────────────────────────────────────────

  handleTap(_localX, localY, state) {
    // Use actual content rect dimensions from the overlay
    const contentRect = state.player.device.appContentRect;
    const appH = contentRect ? contentRect.h : 248;

    // Tap on compose bar → open chat input in aeterna mode
    const composeTop = appH - COMPOSE_BAR_H;
    if (localY >= composeTop) {
      state.chat.aeternaMode = true;
      state.chat.inputOpen = true;
      return true;
    }

    return false;
  },

  // ── Drag scroll ──────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const appState = getAppState(state);
    appState.scrollOffset = Math.max(0, Math.min(appState.scrollOffset + dy, appState.maxScrollOffset));
    appState.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const appState = getAppState(state);
    appState.scrollVelocity = velocityY;
  },

  // ── Lifecycle ─────────────────────────────────────────────────────────

  onOpen(state) {
    const aeterna = state.aeterna;
    if (aeterna.phase !== "dormant") {
      aeterna.panelOpen = true;
      aeterna.panelOpenedAt = Date.now();
      if (aeterna.phase === "idle" || aeterna.phase === "waiting") {
        aeterna.phase = "listening";
        aeterna.phaseAge = 0;
      }
    }
    // Reset scroll to bottom when opening
    const appState = getAppState(state);
    appState.scrollOffset = 0;
    appState.scrollVelocity = 0;
  },

  onClose(state) {
    state.aeterna.panelOpen = false;
    state.chat.aeternaMode = false;
  },

  // ── Badge ─────────────────────────────────────────────────────────────

  badge(state) {
    const conversation = state.aeternaConductor?.getConversation();
    if (conversation && conversation.thinking) return 1;
    if (state.aeterna.phase === "has_something") return 1;
    return 0;
  },
};

// ─── Drawing Helpers ────────────────────────────────────────────────────────

function drawPhaseBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  aeterna: GameState["aeterna"],
  elapsed: number,
  coRegStrategy?: string,
): void {
  // Subtle gradient background — richer than flat fill
  const barGrad = ctx.createLinearGradient(x, y, x, y + PHASE_BAR_H);
  barGrad.addColorStop(0, "rgba(28,18,10,0.9)");
  barGrad.addColorStop(1, "rgba(18,12,6,0.75)");
  ctx.fillStyle = barGrad;
  ctx.fillRect(x, y, w, PHASE_BAR_H);

  // Pulsing presence dot
  const dotPulse = 0.5 + Math.sin(elapsed * 2.4) * 0.35;
  const dotX = x + 13;
  const dotY = y + PHASE_BAR_H / 2;

  // Outer glow
  const dotGlow = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, 11);
  dotGlow.addColorStop(0, `rgba(255,154,60,${0.35 * dotPulse})`);
  dotGlow.addColorStop(1, "rgba(255,154,60,0)");
  ctx.fillStyle = dotGlow;
  ctx.beginPath();
  ctx.arc(dotX, dotY, 11, 0, Math.PI * 2);
  ctx.fill();

  // Core dot
  ctx.fillStyle = C_AMBER;
  ctx.globalAlpha = 0.55 + dotPulse * 0.45;
  ctx.beginPath();
  ctx.arc(dotX, dotY, 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Phase label (primary)
  const phaseLabel = PHASE_LABELS[aeterna.phase] ?? aeterna.phase;
  drawText(ctx, phaseLabel, dotX + 11, y + 4, w - 38, `bold ${FONT_SMALL}`, C_TEXT_DIM);

  // Co-reg hint (secondary, very dim) — gives subtle sense of inner state
  if (coRegStrategy && coRegStrategy !== "expansion") {
    const hint = coRegStrategy === "grounding" ? "holding steady" :
                 coRegStrategy === "containment" ? "holding space" :
                 coRegStrategy === "activation" ? "opening up" : "";
    if (hint) {
      drawText(ctx, hint, dotX + 11, y + 14, w - 38, `italic ${FONT_SMALL}`, "rgba(255,154,60,0.28)");
    }
  }

  // Separator with subtle amber bleed
  const sepGrad = ctx.createLinearGradient(x, y + PHASE_BAR_H, x + w, y + PHASE_BAR_H);
  sepGrad.addColorStop(0, "rgba(255,154,60,0)");
  sepGrad.addColorStop(0.3, "rgba(255,154,60,0.12)");
  sepGrad.addColorStop(0.7, "rgba(255,154,60,0.12)");
  sepGrad.addColorStop(1, "rgba(255,154,60,0)");
  ctx.strokeStyle = sepGrad;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x, y + PHASE_BAR_H);
  ctx.lineTo(x + w, y + PHASE_BAR_H);
  ctx.stroke();
}

function drawComposeBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  isBusy: boolean,
  isInputActive = false,
): void {
  // Top separator with amber bleed
  const sepGrad = ctx.createLinearGradient(x, y, x + w, y);
  sepGrad.addColorStop(0, "rgba(255,154,60,0)");
  sepGrad.addColorStop(0.2, "rgba(255,154,60,0.1)");
  sepGrad.addColorStop(0.8, "rgba(255,154,60,0.1)");
  sepGrad.addColorStop(1, "rgba(255,154,60,0)");
  ctx.strokeStyle = sepGrad;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w, y);
  ctx.stroke();

  // Background with subtle gradient
  const bgGrad = ctx.createLinearGradient(x, y, x, y + COMPOSE_BAR_H);
  bgGrad.addColorStop(0, "rgba(22,14,8,0.8)");
  bgGrad.addColorStop(1, "rgba(14,9,4,0.95)");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(x, y, w, COMPOSE_BAR_H);

  // Input pill
  const padX = 10;
  const inputY = y + 7;
  const inputH = COMPOSE_BAR_H - 14;
  const inputW = w - padX * 2;
  const inputR = inputH / 2;

  // Glow ring when active
  if (isInputActive) {
    drawRoundRect(ctx, x + padX - 1, inputY - 1, inputW + 2, inputH + 2, inputR + 1,
      "rgba(255,154,60,0)", "rgba(255,154,60,0.22)");
  }

  const bgColor = isInputActive ? "rgba(255,245,235,0.10)" : "rgba(255,245,235,0.05)";
  const borderColor = isInputActive ? "rgba(255,154,60,0.30)" : "rgba(255,154,60,0.10)";
  drawRoundRect(ctx, x + padX, inputY, inputW, inputH, inputR, bgColor, borderColor);

  // When input is active, DOM input overlays — don't draw placeholder
  if (!isInputActive) {
    const placeholder = isBusy ? "Aeterna is thinking..." :
                        "What's on your mind?";
    ctx.font = FONT_SMALL;
    ctx.fillStyle = isBusy ? C_AMBER_DIM : "rgba(255,245,235,0.35)";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(placeholder, x + padX + 12, y + COMPOSE_BAR_H / 2);

    // Small send arrow on the right
    if (!isBusy) {
      const arrowX = x + w - padX - 12;
      const arrowY = y + COMPOSE_BAR_H / 2;
      ctx.globalAlpha = 0.2;
      ctx.strokeStyle = C_AMBER;
      ctx.lineWidth = 1.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(arrowX - 5, arrowY);
      ctx.lineTo(arrowX + 2, arrowY);
      ctx.moveTo(arrowX - 1, arrowY - 3);
      ctx.lineTo(arrowX + 2, arrowY);
      ctx.lineTo(arrowX - 1, arrowY + 3);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
}

function drawEmptyState(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  elapsed: number,
): void {
  // Slow, breathing alpha
  const breathe = 0.28 + Math.sin(elapsed * 0.9) * 0.12;

  // Subtle central glow
  const cx = x + w / 2;
  const cy = y + h / 2 - 8;
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.35);
  glow.addColorStop(0, `rgba(255,154,60,${breathe * 0.12})`);
  glow.addColorStop(1, "rgba(255,154,60,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(x, y, w, h);

  ctx.globalAlpha = breathe + 0.1;
  drawText(ctx, "The Aeterna is here.", cx, cy - 10, w - 40, `italic 12px ${FONT_FACE}`, C_AMBER_DIM, "center");
  ctx.globalAlpha = breathe * 0.75;
  drawText(ctx, "Say something to begin.", cx, cy + 12, w - 40, `italic ${FONT_SMALL}`, C_TEXT_DIM, "center");
  ctx.globalAlpha = 1;
}

function drawMessages(
  ctx: CanvasRenderingContext2D,
  x: number, streamY: number, w: number, streamH: number,
  history: readonly AeternaMessage[],
  appState: HearthAppState,
  conductor: { getRevealedText(): string; getConversation(): { typing: { active: boolean } }; getLastCortexState?(): { attentionHighlights?: string[] } | null } | null | undefined,
): void {
  const maxBubbleW = Math.floor(w * MAX_BUBBLE_W_RATIO);
  const textMaxW = maxBubbleW - BUBBLE_INNER_PAD * 2;

  // Get attention highlights — messages the Aeterna is "thinking about"
  const cortex = conductor?.getLastCortexState?.();
  const highlights = cortex?.attentionHighlights ?? [];

  // Measure all bubbles
  const measures: BubbleMeasure[] = [];
  for (const msg of history) {
    const isPlayer = msg.role === "player";
    const isAmbient = msg.ambient === true;
    const font = isAmbient ? `italic ${FONT_SIZE} sans-serif` : FONT;
    const lines = wrapText(ctx, msg.text, textMaxW, font);
    const textH = lines.length * LINE_HEIGHT;
    const height = isAmbient ? textH + 4 : textH + BUBBLE_INNER_PAD * 2;
    measures.push({ lines, height, isPlayer, isAmbient, isThinking: false });
  }

  // Total content height
  let totalH = 0;
  for (let i = 0; i < measures.length; i++) {
    totalH += measures[i].height + BUBBLE_GAP;
  }
  totalH -= BUBBLE_GAP;

  // Update max scroll extent
  appState.maxScrollOffset = Math.max(0, totalH - streamH + BUBBLE_PAD_Y * 2);

  // Draw from bottom, scrolling offset pushes everything up
  let drawY = streamY + streamH - BUBBLE_PAD_Y + appState.scrollOffset;

  // If content fits, anchor to bottom
  if (totalH < streamH - BUBBLE_PAD_Y * 2) {
    drawY = streamY + streamH - BUBBLE_PAD_Y;
  }

  // Render bottom-up (newest at bottom)
  for (let i = history.length - 1; i >= 0; i--) {
    const measure = measures[i];
    const msg = history[i];
    const bubbleH = measure.height;
    const bubbleY = drawY - bubbleH;

    // Cull off-screen
    if (bubbleY + bubbleH < streamY) break;
    if (bubbleY > streamY + streamH) {
      drawY -= bubbleH + BUBBLE_GAP;
      continue;
    }

    if (measure.isAmbient) {
      // Ambient thoughts: gentle italic drift
      ctx.globalAlpha = 0.42;
      let textY = bubbleY;
      ctx.font = `italic ${FONT}`;
      ctx.fillStyle = C_AMBIENT_TEXT;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      for (const line of measure.lines) {
        ctx.fillText(line, x + BUBBLE_PAD_X + 6, textY);
        textY += LINE_HEIGHT;
      }
      ctx.globalAlpha = 1;
    } else {
      // Regular message bubble
      const isPlayer = measure.isPlayer;
      const bubbleW = Math.min(maxBubbleW, getBubbleWidth(ctx, measure.lines, FONT) + BUBBLE_INNER_PAD * 2);
      const bubbleX = isPlayer
        ? x + w - BUBBLE_PAD_X - bubbleW  // right-aligned
        : x + BUBBLE_PAD_X;               // left-aligned

      // Bubble background — gradient for Aeterna (warm depth), flat for player
      if (isPlayer) {
        drawRoundRect(ctx, bubbleX, bubbleY, bubbleW, bubbleH, BUBBLE_RADIUS, C_PLAYER_BG, C_PLAYER_BORDER);
      } else {
        // Aeterna: rich warm gradient
        const aGrad = ctx.createLinearGradient(bubbleX, bubbleY, bubbleX, bubbleY + bubbleH);
        aGrad.addColorStop(0, "rgba(38,26,16,0.92)");
        aGrad.addColorStop(1, "rgba(26,16,8,0.88)");
        ctx.fillStyle = aGrad;
        ctx.save();
        ctx.beginPath();
        // Rounded rect path manually (drawRoundRect doesn't return a path)
        const r = BUBBLE_RADIUS;
        ctx.moveTo(bubbleX + r, bubbleY);
        ctx.lineTo(bubbleX + bubbleW - r, bubbleY);
        ctx.quadraticCurveTo(bubbleX + bubbleW, bubbleY, bubbleX + bubbleW, bubbleY + r);
        ctx.lineTo(bubbleX + bubbleW, bubbleY + bubbleH - r);
        ctx.quadraticCurveTo(bubbleX + bubbleW, bubbleY + bubbleH, bubbleX + bubbleW - r, bubbleY + bubbleH);
        ctx.lineTo(bubbleX + r, bubbleY + bubbleH);
        ctx.quadraticCurveTo(bubbleX, bubbleY + bubbleH, bubbleX, bubbleY + bubbleH - r);
        ctx.lineTo(bubbleX, bubbleY + r);
        ctx.quadraticCurveTo(bubbleX, bubbleY, bubbleX + r, bubbleY);
        ctx.closePath();
        ctx.fill();
        // Border
        ctx.strokeStyle = C_AETERNA_BORDER;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.restore();
      }

      // Attention glow — subtle warm highlight on attended messages
      const isAttended = highlights.length > 0 && highlights.some(h => msg.text.includes(h) || h.includes(msg.text));
      if (isAttended) {
        ctx.save();
        ctx.globalAlpha = 0.1;
        ctx.shadowColor = C_AMBER;
        ctx.shadowBlur = 12;
        drawRoundRect(ctx, bubbleX - 1, bubbleY - 1, bubbleW + 2, bubbleH + 2, BUBBLE_RADIUS + 1, C_AMBER_FAINT);
        ctx.restore();
      }

      // Aeterna accent bar — warm left edge with glow
      if (!isPlayer) {
        // Glow behind the bar
        const barGlow = ctx.createLinearGradient(bubbleX, bubbleY, bubbleX + 6, bubbleY);
        barGlow.addColorStop(0, "rgba(255,154,60,0.18)");
        barGlow.addColorStop(1, "rgba(255,154,60,0)");
        ctx.fillStyle = barGlow;
        ctx.fillRect(bubbleX, bubbleY + 3, 6, bubbleH - 6);
        // Bar itself
        ctx.fillStyle = C_AMBER;
        ctx.globalAlpha = 0.45;
        drawRoundRect(ctx, bubbleX, bubbleY + 5, 2, bubbleH - 10, 1, C_AMBER);
        ctx.globalAlpha = 1;
      }

      // Message text
      const isCurrentlyTyping = !isPlayer && i === history.length - 1 &&
        conductor?.getConversation().typing.active;

      const displayText = isCurrentlyTyping
        ? conductor!.getRevealedText()
        : msg.text;

      const displayLines = isCurrentlyTyping
        ? wrapText(ctx, displayText, textMaxW, FONT)
        : measure.lines;

      const textX = bubbleX + BUBBLE_INNER_PAD + (isPlayer ? 0 : 4);
      let textY = bubbleY + BUBBLE_INNER_PAD;
      ctx.font = FONT;
      // Player text slightly cooler, Aeterna text warm white
      ctx.fillStyle = isPlayer ? "rgba(210,225,255,0.92)" : C_TEXT_PRIMARY;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      for (const line of displayLines) {
        ctx.fillText(line, textX, textY);
        textY += LINE_HEIGHT;
      }

      // Proactive badge (small amber dot for unprompted messages)
      if (msg.proactive) {
        ctx.fillStyle = C_AMBER;
        ctx.globalAlpha = 0.45;
        ctx.beginPath();
        ctx.arc(bubbleX + bubbleW - 7, bubbleY + 8, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    drawY -= bubbleH + BUBBLE_GAP;
  }
}

function drawThinkingIndicator(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  elapsed: number, _thinkingText: string,
): void {
  // Three animated dots, left-aligned like an Aeterna message
  const baseX = x + BUBBLE_PAD_X + 12;
  const centerY = y + 10;

  for (let d = 0; d < 3; d++) {
    const phase = elapsed * 3 + d * 0.7;
    const dotAlpha = 0.3 + Math.sin(phase) * 0.35;
    const dotOffset = Math.sin(phase) * 1.5;
    ctx.fillStyle = C_AMBER;
    ctx.globalAlpha = Math.max(0.15, dotAlpha);
    ctx.beginPath();
    ctx.arc(baseX + d * 8, centerY + dotOffset, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function getBubbleWidth(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  font: string,
): number {
  ctx.font = font;
  let maxW = 0;
  for (const line of lines) {
    const w = ctx.measureText(line).width;
    if (w > maxW) maxW = w;
  }
  return Math.ceil(maxW);
}

// ─── Self-Register ──────────────────────────────────────────────────────────

registerApp(hearthApp);
